/**
 * Bitbucket Integration API
 *
 * API endpoints for managing Bitbucket OAuth connections and credentials
 */

import fetchWithAuth from "@/api/secureFetch";

const AGENT_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export interface BitbucketCredentialsCheckResponse {
    hasCredentials: boolean;
    workspace?: string;
    username?: string;
    isConnected?: boolean;
}

export interface InitiateBitbucketOAuthRequest {
    userId: number;
    workspace?: string;
}

export interface InitiateBitbucketOAuthResponse {
    authUrl?: string;
    authorizationUrl?: string;
    state: string;
}

export interface BitbucketOAuthCallbackParams {
    code: string;
    state: string;
}

export interface BitbucketOAuthCallbackResponse {
    success: boolean;
    message?: string;
    error?: string;
    accessToken?: string;
}

/**
 * Check if user has existing Bitbucket credentials
 */
export async function checkBitbucketCredentials(
    userId: number,
    token: string
): Promise<BitbucketCredentialsCheckResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/integrations/status?userId=${userId}&vendor=bitbucket`,
        token,
        {
            method: "GET"
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to check credentials: ${response.status}`);
    }

    return response.json();
}

/**
 * Initiate OAuth flow for Bitbucket
 */
export async function initiateBitbucketOAuth(
    request: InitiateBitbucketOAuthRequest,
    token: string
): Promise<InitiateBitbucketOAuthResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/git/bitbucket/oauth/authorize`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request)
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to initiate OAuth: ${response.status}`);
    }

    return response.json();
}

/**
 * Handle OAuth callback
 */
export async function handleBitbucketOAuthCallback(
    params: BitbucketOAuthCallbackParams,
    token: string
): Promise<BitbucketOAuthCallbackResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/git/bitbucket/oauth/callback?code=${params.code}&state=${params.state}`,
        token,
        {
            method: "GET"
        }
    );

    if (!response.ok) {
        throw new Error(`OAuth callback failed: ${response.status}`);
    }

    return response.json();
}
